
import React from 'react';
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";
import { ModeToggle } from "@/components/rate-whisperer/ModeToggle";
import { PageHeader } from "@/components/rate-whisperer/PageHeader";
import { StepIndicator } from "@/components/rate-whisperer/StepIndicator";
import { RateWhispererRenderer } from "@/components/rate-whisperer/RateWhispererRenderer";
import { CrystalBallRenderer } from "@/components/rate-whisperer/CrystalBallRenderer";
import { useRateWhispererForm } from "@/components/rate-whisperer/hooks/useRateWhispererForm";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Info } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";

const RateWhispererPage = () => {
  const {
    currentStep,
    crystalBallMode,
    promptMode,
    formData,
    crystalBallData,
    setCrystalBallMode,
    setPromptMode,
    setFormData,
    setCrystalBallData,
    setCurrentStep,
    handleInputChange,
    handleNext,
    handleBack,
    handleResultsFromPrompt,
    resetForm,
  } = useRateWhispererForm();
  
  const isMobile = useIsMobile();

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-card-bg/50 to-background">
      <div className="container mx-auto px-3 sm:px-4 py-6 sm:py-8 md:py-12 animate-fade-in">
        <div className="max-w-4xl mx-auto">
          <PageHeader crystalBallMode={crystalBallMode} />
          
          <div className="flex flex-col mb-8 sm:mb-12">
            <TooltipProvider>
              <div className="flex items-center justify-center gap-3">
                <ModeToggle
                  crystalBallMode={crystalBallMode}
                  setCrystalBallMode={setCrystalBallMode}
                  setFormData={setFormData}
                  setCrystalBallData={setCrystalBallData}
                  setCurrentStep={setCurrentStep}
                />
                
                {!isMobile && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-10 w-10 rounded-full bg-card-bg/50 border border-primary/20 hover:border-primary/50 transition-all duration-300">
                        <Info className="h-5 w-5 text-primary" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side="top" className="max-w-xs p-3 bg-card-bg border border-primary/20">
                      <p className="text-sm text-text-primary">
                        {!crystalBallMode ? 
                          "💡 Calculate your hourly rate based on your finances and time off." : 
                          "🔮 Project earnings based on your desired rates and utilization."}
                      </p>
                    </TooltipContent>
                  </Tooltip>
                )}
              </div>
            </TooltipProvider>
            
            {/* Enhanced Input Mode Toggle */}
            {!crystalBallMode && (
              <div className="flex justify-center mt-6">
                <div className="inline-flex p-1 rounded-2xl bg-card-bg border-2 border-primary/20 shadow-lg">
                  <Button
                    variant={promptMode ? "ghost" : "default"}
                    size={isMobile ? "sm" : "default"}
                    onClick={() => setPromptMode(false)}
                    className={`px-6 py-2 rounded-xl transition-all duration-300 ${
                      !promptMode 
                        ? 'bg-gradient-to-r from-primary to-secondary text-white shadow-md' 
                        : 'text-text-secondary hover:text-text-primary hover:bg-card-hover'
                    }`}
                  >
                    Step-by-Step
                  </Button>
                  <Button 
                    variant={!promptMode ? "ghost" : "default"}
                    size={isMobile ? "sm" : "default"}
                    onClick={() => setPromptMode(true)}
                    className={`px-6 py-2 rounded-xl transition-all duration-300 ${
                      promptMode 
                        ? 'bg-gradient-to-r from-primary to-secondary text-white shadow-md' 
                        : 'text-text-secondary hover:text-text-primary hover:bg-card-hover'
                    }`}
                  >
                    {isMobile ? 'Describe' : 'Describe Your Situation'}
                  </Button>
                </div>
              </div>
            )}
          </div>

          <StepIndicator 
            currentStep={currentStep} 
            totalSteps={crystalBallMode ? 5 : 4}
            promptMode={!crystalBallMode && promptMode}
          />

          {/* Enhanced Card with backdrop blur */}
          <div className="relative">
            <div className="absolute inset-0 bg-gradient-to-r from-primary/10 to-secondary/10 rounded-3xl blur-2xl"></div>
            <Card className="relative p-6 sm:p-8 md:p-10 animate-fade-in bg-card-bg/80 backdrop-blur-sm border-2 border-primary/20 rounded-3xl shadow-2xl">
              {currentStep > 1 && !(!crystalBallMode && promptMode && currentStep === 4) && (
                <Button
                  variant="ghost"
                  onClick={handleBack}
                  className="mb-6 sm:mb-8 text-text-secondary hover:text-text-primary transition-all duration-300 hover:bg-card-hover rounded-xl"
                  size={isMobile ? "sm" : "default"}
                >
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Back
                </Button>
              )}
              
              {crystalBallMode ? (
                <CrystalBallRenderer
                  currentStep={currentStep}
                  formData={crystalBallData}
                  onInputChange={handleInputChange}
                  onNext={handleNext}
                  onStartOver={resetForm}
                />
              ) : (
                <RateWhispererRenderer
                  currentStep={currentStep}
                  formData={formData}
                  promptMode={promptMode}
                  onInputChange={handleInputChange}
                  onNext={handleNext}
                  onStartOver={resetForm}
                  onResultsFromPrompt={handleResultsFromPrompt}
                />
              )}
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RateWhispererPage;
